"use client"

import { useState, useEffect } from "react"
import { Gift, Ticket, Loader2, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { toast } from "sonner"
import { useVerifyReferralCodeMutation, useApplyReferralCodeMutation } from "@/app/store/api/authApi"

interface ReferralCodeDialogProps {
    isOpen: boolean
    onClose: () => void
    initialCode?: string
    onApply?: (code: string, referrerName: string) => void
    onSkip?: () => void
    showApplyToAccount?: boolean // For dashboard use - applies code to user's account
}

export function ReferralCodeDialog({
    isOpen,
    onClose,
    initialCode = "",
    onApply,
    onSkip,
    showApplyToAccount = false
}: ReferralCodeDialogProps) {
    const [referralInputCode, setReferralInputCode] = useState(initialCode)
    const [referrerName, setReferrerName] = useState<string | null>(null)
    const [isReferralValid, setIsReferralValid] = useState(false)
    const [isVerifyingCode, setIsVerifyingCode] = useState(false)
    const [verifyReferralCode] = useVerifyReferralCodeMutation()
    const [applyReferralCode, { isLoading: isApplying }] = useApplyReferralCodeMutation()

    // Auto-verify if initial code is provided
    useEffect(() => {
        if (initialCode && isOpen) {
            setReferralInputCode(initialCode.toUpperCase())
            handleVerifyCode(initialCode)
        }
    }, [initialCode, isOpen])

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setReferralInputCode(initialCode || "")
            setReferrerName(null)
            setIsReferralValid(false)
        }
    }, [isOpen, initialCode])

    const handleVerifyCode = async (code?: string) => {
        const codeToVerify = code || referralInputCode.trim()
        if (!codeToVerify) {
            toast.error("Please enter a referral code")
            return
        }
        setIsVerifyingCode(true)
        try {
            const res = await verifyReferralCode({ referralCode: codeToVerify }).unwrap()
            if (res.success && res.user) {
                const fullName = `${res.user.firstName} ${res.user.lastName || ''}`.trim()
                setReferrerName(fullName)
                setIsReferralValid(true)
                toast.success("Referral code verified!")
            } else {
                toast.error("Invalid referral code")
                setIsReferralValid(false)
                setReferrerName(null)
            }
        } catch {
            toast.error("Invalid referral code")
            setIsReferralValid(false)
            setReferrerName(null)
        } finally {
            setIsVerifyingCode(false)
        }
    }

    const handleApply = async () => {
        if (!isReferralValid || !referrerName) {
            if (referralInputCode.trim()) {
                await handleVerifyCode()
            }
            return
        }

        // If showApplyToAccount is true, apply the code to the user's account
        if (showApplyToAccount) {
            try {
                await applyReferralCode({ referralCode: referralInputCode.trim().toUpperCase() }).unwrap()
                toast.success("Referral code applied to your account!")
            } catch (error: any) {
                toast.error(error?.data?.message || "Failed to apply referral code")
                return
            }
        }

        onApply?.(referralInputCode.trim().toUpperCase(), referrerName)
        onClose()
    }

    const handleSkip = () => {
        onSkip?.()
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="bg-gradient-to-br from-blue-400 to-blue-500 p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                        {isReferralValid ? (
                            <Gift className="w-7 h-7 text-white" />
                        ) : (
                            <Ticket className="w-7 h-7 text-white" />
                        )}
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">
                        {isReferralValid ? `${referrerName} invited you!` : 'Have a Referral Code?'}
                    </h2>
                    <p className="text-blue-100 text-sm">
                        {isReferralValid
                            ? 'Get 1 free fast deck on your account'
                            : 'Enter your code to get 1 free fast deck'}
                    </p>
                </div>
                <div className="p-5 space-y-4">
                    {/* Referral Code Input */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                            Referral Code
                        </label>
                        <div className="flex gap-2">
                            <Input
                                value={referralInputCode}
                                onChange={(e) => {
                                    setReferralInputCode(e.target.value.toUpperCase())
                                    // Reset validation if code changes
                                    if (isReferralValid && e.target.value.toUpperCase() !== referralInputCode) {
                                        setIsReferralValid(false)
                                        setReferrerName(null)
                                    }
                                }}
                                placeholder="Enter code"
                                className="flex-1 font-mono text-center font-bold tracking-wider uppercase"
                                disabled={isVerifyingCode || isApplying}
                            />
                            <Button
                                type="button"
                                onClick={() => handleVerifyCode()}
                                disabled={isVerifyingCode || !referralInputCode.trim() || isApplying}
                                className="bg-blue-600 hover:bg-blue-700 px-4"
                            >
                                {isVerifyingCode ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Verify'
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Verified Status */}
                    {isReferralValid && referrerName && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                                <Check className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-emerald-800">Code verified!</p>
                                <p className="text-xs text-emerald-600">Referred by {referrerName}</p>
                            </div>
                        </div>
                    )}

                    {/* Bonus Info */}
                    <div className="bg-blue-50 rounded-lg px-4 py-3 text-center">
                        <p className="text-sm text-slate-600">
                            You'll get <span className="font-bold text-blue-600">1 free fast deck</span> when you {showApplyToAccount ? 'apply a valid code' : 'sign up with a valid code'}!
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleSkip}
                            className="flex-1"
                            disabled={isApplying}
                        >
                            Skip
                        </Button>
                        <Button
                            type="button"
                            onClick={handleApply}
                            disabled={isVerifyingCode || isApplying}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                            {isApplying ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Applying...
                                </>
                            ) : isReferralValid ? (
                                'Continue'
                            ) : (
                                'Apply & Continue'
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
